import {
  Badge,
  Button,
  CardClose,
  CardFooter,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
  useIsMobile,
} from '@ais-ui/ui-core';
import { TargetTypeEnum } from '@ais-ui/websockets';
import React from 'react';
import { TargetActions } from '../Map/ShipActions/TargetActions';
import { TabsDetails } from './ShipDetailsTabs/TabsDetails';
import {
  PiArrowsClockwise,
  PiArrowSquareOut,
  PiBoat,
  PiCellSignalSlash,
} from 'react-icons/pi';
import {
  useViewMode,
  ViewMode,
} from '../../components/ViewModeProvider';
import { parseSecondsToCustomDuration } from '../utils/formatDuration';
import { TargetInfoDetails } from './TargetInfoDetails';
import { useTargetContext } from './TargetContext';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useSettings } from '../Settings/SettingsProvider';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { AisReportStatus } from './useAISReportAge';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';
import { TargetCardTargetType } from '../Settings/Target/useTargetForm';
import {
  getFieldDisplayName,
  getFieldIcon,
} from '../Settings/Target/fieldHelpers';

// Map TargetTypeEnum to TargetCardTargetType for settings lookup
const mapTargetTypeToSettingsKey = (
  targetType: TargetTypeEnum,
): TargetCardTargetType => {
  switch (targetType) {
    case TargetTypeEnum.Ship:
    case TargetTypeEnum.ExtendedVessel:
      return 'vessel';
    case TargetTypeEnum.Aton:
      return 'aton';
    case TargetTypeEnum.VirtualAton:
      return 'virtualAton';
    case TargetTypeEnum.Methyd:
      return 'methyd';
    case TargetTypeEnum.BaseStation:
      return 'baseStation';
    case TargetTypeEnum.Radar:
      return 'radar';
    case TargetTypeEnum.Das:
      return 'das';
    case TargetTypeEnum.Drone:
      return 'drone';
    default:
      return 'vessel';
  }
};

// Helper function to get field value from target info (supports all target types)
const getFieldValue = (
  targetInfo: any,
  fieldName: string,
  formatDateTimeWithTimeZone?: (date: any) => string,
): string => {
  if (!targetInfo || !fieldName) return '';

  // Date fields that need formatting
  const dateFields = [
    'eta',
    'lastAisReport',
    'lastAisSatReport',
    'portEta',
    'lastRadarReport',
    'lastReportTimestamp',
    'reportTimestamp',
  ];

  if (dateFields.includes(fieldName) && formatDateTimeWithTimeZone) {
    const value = targetInfo[fieldName];
    return value ? formatDateTimeWithTimeZone(value) : '';
  }

  // Boolean fields that need special display
  if (fieldName === 'offPosition') {
    return targetInfo.offPosition ? 'Yes' : 'No';
  }

  // Return direct property value with fallback
  const value = targetInfo[fieldName];
  if (value === undefined || value === null) return '';
  return String(value);
};

export const TargetCardContent = () => {
  const {
    canShowDetailedInformation,
    multipleTargetsSelected,
    tab,
    setTab,
    aisReportAgeResult,
    nextTargetName,
    onExit,
    name,
    isExpanded,
    setIsExpanded,
  } = useTargetContext();
  const { currentTargetInfo, currentTargetType, goToNextCard } =
    useSelectionState();
  const { viewMode, setViewMode } = useViewMode();
  const { settings } = useSettings();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const isMobile = useIsMobile();

  // Determine the settings key based on the current target type
  const settingsKey = currentTargetType
    ? mapTargetTypeToSettingsKey(currentTargetType)
    : 'vessel';

  // Get the configured header and subtitle fields from settings (per target type)
  const headerFieldSettings =
    settings['ribbonPlugin.settings']?.target_header_field;
  const subtitleFieldSettings =
    settings['ribbonPlugin.settings']?.target_subtitle_field;
  const favoriteFieldsSettings =
    settings['ribbonPlugin.settings']?.favorite_fields;

  // Support both old (string) and new (object) format for backward compatibility
  const headerField =
    typeof headerFieldSettings === 'object' && headerFieldSettings !== null
      ? (headerFieldSettings as Record<string, string>)[settingsKey] || 'name'
      : headerFieldSettings || 'name';

  const subtitleField =
    typeof subtitleFieldSettings === 'object' && subtitleFieldSettings !== null
      ? (subtitleFieldSettings as Record<string, string>)[settingsKey] ||
        'callSign'
      : subtitleFieldSettings || 'callSign';

  // Get the favorite fields from settings (per target type)
  const favoriteFields: string[] =
    typeof favoriteFieldsSettings === 'object' &&
    favoriteFieldsSettings !== null &&
    !Array.isArray(favoriteFieldsSettings)
      ? ((favoriteFieldsSettings as Record<string, string[]>)[settingsKey] || [])
      : Array.isArray(favoriteFieldsSettings)
        ? favoriteFieldsSettings
        : [];

  // Get the header and subtitle values
  const headerValue =
    currentTargetType === TargetTypeEnum.Das
      ? (() => {
          const dasInfo = currentTargetInfo as any;
          const site = dasInfo.site || 'Unknown Site';
          const trackId = dasInfo.trackId || 'Unknown Track';
          return `${site} ${trackId}`;
        })()
      : getFieldValue(
          currentTargetInfo,
          headerField,
          formatDateTimeWithTimeZone,
        );

  const subtitleValue =
    currentTargetType === TargetTypeEnum.Das
      ? 'DAS'
      : currentTargetType === TargetTypeEnum.Drone
        ? 'Drone'
        : getFieldValue(
            currentTargetInfo,
            subtitleField,
            formatDateTimeWithTimeZone,
          );

  // Get the ship type for the badge - hide badge for DAS and Drone
  const shipType =
    currentTargetType === TargetTypeEnum.Das ||
    currentTargetType === TargetTypeEnum.Drone
      ? null
      : getFieldValue(currentTargetInfo, 'shipType') || 'Unknown Ship Type';

  // If no target info or type, return null
  if (!currentTargetInfo || !currentTargetType) {
    return null;
  }

  return (
    <>
      <CardHeader variant="tinted" className="pb-4">
        {/* Exit button - positioned like other panels */}
        {!isMobile && <CardClose onClick={onExit} />}
        {/* Action buttons positioned to the left of close button */}
        {!isMobile && (
          <div className="absolute right-12 top-4 flex items-center gap-3">
            {multipleTargetsSelected && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={goToNextCard}
                  >
                    <PiArrowsClockwise className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {`Go to next target: ${nextTargetName}`}
                </TooltipContent>
              </Tooltip>
            )}
            {canShowDetailedInformation && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const targetInfo = currentTargetInfo as any;
                      const imo = targetInfo?.imo || targetInfo?.basic?.vesselImoNumber;
                      const mmsi = targetInfo?.mmsi || targetInfo?.basic?.mmsi;
                      const vesselName = targetInfo?.name || targetInfo?.basic?.vesselName || name;
                      if (imo) {
                        openPopupOnSecondaryScreen(PopupType.VesselTimeline, {
                          imo: String(imo),
                          mmsi: mmsi ? String(mmsi) : '',
                          name: vesselName || '',
                          shipInfo: JSON.stringify({
                            name: targetInfo?.name,
                            imo: targetInfo?.imo,
                            mmsi: targetInfo?.mmsi,
                            callSign: targetInfo?.callSign,
                            country: targetInfo?.country,
                            aisType: targetInfo?.aisType,
                            sourceType: targetInfo?.sourceType,
                            shipType: targetInfo?.shipType,
                            width: targetInfo?.width,
                            length: targetInfo?.length,
                            draught: targetInfo?.draught,
                            heading: targetInfo?.heading,
                            rateOfTurn: targetInfo?.rateOfTurn,
                            courseOverGround: targetInfo?.courseOverGround,
                            speedOverGround: targetInfo?.speedOverGround,
                            destination: targetInfo?.destination,
                            navigationalStatus: targetInfo?.navigationalStatus,
                            eta: targetInfo?.eta?.toISOString?.() || targetInfo?.eta,
                            portEta: targetInfo?.portEta?.toISOString?.() || targetInfo?.portEta,
                            lastAisLatitude: targetInfo?.lastAisLatitude,
                            lastAisLongitude: targetInfo?.lastAisLongitude,
                            lastAisReport: targetInfo?.lastAisReport?.toISOString?.() || targetInfo?.lastAisReport,
                            lastAisSatLatitude: targetInfo?.lastAisSatLatitude,
                            lastAisSatLongitude: targetInfo?.lastAisSatLongitude,
                            lastAisSatReport: targetInfo?.lastAisSatReport?.toISOString?.() || targetInfo?.lastAisSatReport,
                          }),
                        });
                      }
                    }}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <PiArrowSquareOut className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>View timeline and details</TooltipContent>
              </Tooltip>
            )}
            {setIsExpanded && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    {isExpanded ? (
                      <ChevronUp className="size-4" />
                    ) : (
                      <ChevronDown className="size-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isExpanded ? 'Collapse target card' : 'Expand target card'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
        <div className="flex justify-between items-start">
          <div className="flex flex-col space-y-2">
            <div className="flex pr-4 space-x-3 items-center">
              <div>
                <CardTitle className="flex text-lg font-medium text-muted-semibold space-x-4">
                  <TruncatedText text={headerValue || 'Unknown'} maxWidth="200px" />
                  {aisReportAgeResult && aisReportAgeResult.status !== AisReportStatus.Fresh && (
                    <Tooltip>
                      <TooltipTrigger>
                        <PiCellSignalSlash 
                          className={`h-6 w-6 ${
                            aisReportAgeResult.status === AisReportStatus.Removed
                              ? 'text-red-600'
                              : 'text-yellow-600'
                          }`}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        Last AIS message is{' '}
                        {parseSecondsToCustomDuration(
                          aisReportAgeResult.aisReportAge,
                        )}{' '}
                        old
                      </TooltipContent>
                    </Tooltip>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{subtitleValue}</p>
              </div>
            </div>
            {shipType && (
              <Badge className="text-xs w-fit space-x-2" variant={'secondary'}>
                <PiBoat className="h-3 w-3"></PiBoat>
                <p>{shipType}</p>
              </Badge>
            )}
          </div>
          <div className="flex flex-col space-y-2 mt-6">
            {currentTargetType === TargetTypeEnum.Ship ||
            currentTargetType === TargetTypeEnum.ExtendedVessel ? (
              <TabsDetails
                tab={tab}
                setTab={setTab}
                imo={(currentTargetInfo as any)?.imo}
              />
            ) : null}
          </div>
        </div>
        {/* Separator - Only show if there are favorite fields */}
        {favoriteFields.length > 0 && (
          <div className="pt-3">
            <div className="border-t border-border"></div>
          </div>
        )}

        {/* Favorite Fields - Full width spanning entire card header */}
        {favoriteFields.length > 0 && (
          <div className="pt-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {favoriteFields.map((fieldName: string) => {
                const fieldValue = getFieldValue(
                  currentTargetInfo,
                  fieldName,
                  formatDateTimeWithTimeZone,
                );
                const IconComponent = getFieldIcon(fieldName);

                return (
                  <div
                    key={fieldName}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Tooltip>
                      <TooltipTrigger className="select-none">
                        <IconComponent className="size-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        {getFieldDisplayName(fieldName)}
                      </TooltipContent>
                    </Tooltip>
                    <span className="truncate whitespace-nowrap select-text inline-block">
                      {fieldValue || '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <TargetInfoDetails
          targetInfo={currentTargetInfo}
          targetType={currentTargetType}
          tab={tab}
        />
      )}
      {/* Hide action buttons (Create Filter, Last Port Call, etc.) on mobile */}
      {isExpanded && !isMobile && (
        <CardFooter className="">
          <TargetActions
            targetInfo={currentTargetInfo}
            targetType={currentTargetType}
            viewMode={viewMode}
          ></TargetActions>
        </CardFooter>
      )}
    </>
  );
};
